import {
  type AppearanceChoice,
  appearanceKey,
  DARK_SCHEME_QUERY,
  LAST_USER_KEY,
  parseChoice,
  resolveTheme,
} from './appearance';

export interface AppearanceEnvironment {
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  darkScheme: Pick<MediaQueryList, 'matches' | 'addEventListener' | 'removeEventListener'>;
  root: { dataset: DOMStringMap };
}

export interface AppearanceController {
  choice(userId: string): AppearanceChoice;
  /** Applies `userId`'s choice and keeps following the OS while it is System. */
  activate(userId: string): void;
  /** Removes the theme; `forget` also stops the inline script applying it on the next load. */
  deactivate(options?: { forget?: boolean }): void;
  choose(userId: string, choice: AppearanceChoice): void;
}

export function createAppearanceController({
  storage,
  darkScheme,
  root,
}: AppearanceEnvironment): AppearanceController {
  let activeUser: string | null = null;

  const choice = (userId: string): AppearanceChoice =>
    parseChoice(read(storage, appearanceKey(userId)));

  const apply = (): void => {
    if (activeUser === null) return;
    root.dataset.theme = resolveTheme(choice(activeUser), darkScheme.matches);
  };

  return {
    choice,
    activate(userId) {
      if (activeUser === null) darkScheme.addEventListener('change', apply);
      activeUser = userId;
      write(storage, LAST_USER_KEY, userId);
      apply();
    },
    deactivate({ forget = false } = {}) {
      if (activeUser !== null) darkScheme.removeEventListener('change', apply);
      activeUser = null;
      delete root.dataset.theme;
      if (forget) remove(storage, LAST_USER_KEY);
    },
    choose(userId, next) {
      write(storage, appearanceKey(userId), next);
      if (userId === activeUser) apply();
    },
  };
}

let browserController: AppearanceController | undefined;

export function browserAppearance(): AppearanceController {
  browserController ??= createAppearanceController({
    storage: window.localStorage,
    darkScheme: window.matchMedia(DARK_SCHEME_QUERY),
    root: document.documentElement,
  });
  return browserController;
}

// Storage can be unavailable (private modes, blocked cookies). Losing the
// choice falls back to System, which is a worse experience, not a broken one.
function read(storage: AppearanceEnvironment['storage'], key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function write(storage: AppearanceEnvironment['storage'], key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    // See read.
  }
}

function remove(storage: AppearanceEnvironment['storage'], key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // See read.
  }
}
